import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useApiGetAccountingStripeRecap from "./api-hooks/useApiGetAccountingStripeRecap";
import useApiGetAccountingCashRecap from "./api-hooks/useApiGetAccountingCashRecap";
import useApiGetAccountingBookingRecap from "./api-hooks/useApiGetAccountingBookingRecap";
import useApiGetCashTransactionsDetail from "./api-hooks/useApiGetCashTransactionsDetail";
import Loading from "../../ui/Loading";
import React from "react";
import useApiGetStripeTransactionsDetail from "./api-hooks/useApiGetStripeTransactionsDetail";
import useApiGetBookingsDetail from "./api-hooks/useApiGetBookingsDetail";
import useApiUpdateBookingStatus from "./api-hooks/useApiUpdateBookingStatus";
import { IoDownloadOutline, IoPeopleOutline, IoChatbubbleEllipsesOutline, IoStatsChartOutline } from "react-icons/io5";
import RecapTypeTabs from "./components/RecapTypeTabs";
import YearMonthPicker from "./components/YearMonthPicker";
import ExpandIcon from "./components/ExpandIcon";
import CashDetailTable from "./components/CashDetailTable";
import StripeDetailTable from "./components/StripeDetailTable";
import BookingDetailTable from "./components/BookingDetailTable";
import DailyTrendChart from "./components/DailyTrendChart";
import { toCSV, downloadCSV, buildCsvRowsForRestaurant } from "./utils/csv";
import useApiUpdateBillingData from "./api-hooks/useApiUpdateBillingData";
import useApiGetPaymentMethods from "../../api-hooks/useApiGetPaymentMethods";
import useApiCreateBillingData from "./api-hooks/useApiCreateBillingData";
import useApiUpdateNotAccountedNote from "./api-hooks/useApiUpdateNotAccountedNote";
import BillingEditForm from "./components/BillingEditForm";
import CreateBillingModal from "./components/CreateBillingModal";
import EditNoteModal from "./components/EditNoteModal";
import {useNavigate} from "react-router-dom";
import useApiGetListRestaurants from "./api-hooks/useApiGetListRestaurants";

type RecapType = "stripe" | "cash" | "booking";

function AccountingRecapPage() {
    const now = new Date();
    const currentYearNum = now.getFullYear();
    const currentMonthNum = now.getMonth() + 1;

    const currentYear = new Date().getFullYear().toString();
    const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, "0");

    const [selectedYear, setSelectedYear] = useState<string>(currentYear);
    const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth);
    const [selectedRecap, setSelectedRecap] = useState<RecapType>("stripe");
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const [selectedRestaurantId, setSelectedRestaurantId] = useState<number | null>(null);
    const [expandedRestaurants, setExpandedRestaurants] = useState<Record<number, boolean>>({});
    const [restaurantFilter, setRestaurantFilter] = useState<string>("");
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [showTrend, setShowTrend] = useState<boolean>(false);
    const restaurantsQuery = useApiGetListRestaurants({ enabled: true });
    const restaurants = restaurantsQuery.data || [];
    const isRestaurantsLoading = restaurantsQuery.isLoading;
    const isRestaurantsError = restaurantsQuery.isError;
    const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [createForm, setCreateForm] = useState<{
        ris_id: number | "";
        cisco_id: string | null;
        company_name: string;
        contact_name: string;
        address: string | null;
        city: string | null;
        zip: string | null;
        province: string | null;
        vat: string | null;
        tax_id: string | null;
        payment_code: string;
        payment_method: string;
        unique_email: string | null;
    }>({
        ris_id: "",
        cisco_id: "",
        company_name: "",
        contact_name: "",
        address: "",
        city: "",
        zip: "",
        province: "",
        vat: "",
        tax_id: "",
        payment_code: "",
        payment_method: "",
        unique_email: "",
    });

    const [editingCustomerId, setEditingCustomerId] = useState<number | null>(null);
    const [editForm, setEditForm] = useState<{
        cisco_id: string | null;
        name: string;
        address: string | null;
        city: string | null;
        zip: string | null;
        province: string | null;
        vat: string | null;
        tax_id: string | null;
        payment_code: string;
        payment_method: string;
        unique_email: string | null;
    }>({
        cisco_id: "",
        name: "",
        address: "",
        city: "",
        zip: "",
        province: "",
        vat: "",
        tax_id: "",
        payment_code: "",
        payment_method: "",
        unique_email: "",
    });
    const [editError, setEditError] = useState<string | null>(null);
    const [isNoteModalOpen, setIsNoteModalOpen] = useState<boolean>(false);
    const [currentNote, setCurrentNote] = useState<string>("");
    const [currentNoteRisId, setCurrentNoteRisId] = useState<number | null>(null);

    const paymentMethodsQuery = useApiGetPaymentMethods({
        enabled: true,
    });

    const paymentMethods = paymentMethodsQuery.data;
    const isPmLoading = paymentMethodsQuery.isLoading;
    const isPmError = paymentMethodsQuery.isError;
    const pmList: any[] = Array.isArray(paymentMethods) ? paymentMethods : Object.values(paymentMethods || {});

   useEffect(() => {
        if (!editingCustomerId || !paymentMethods) return;
        const current = editForm.payment_code;
        if (current && paymentMethods.some(pm => pm.official_code === current)) return;
        if (current) {
            const match = paymentMethods.find(pm => pm.official_code === current);
            if (match) {
                setEditForm(prev => ({ ...prev, payment_code: match.code }));
            }
        }
    }, [editingCustomerId, paymentMethods, editForm.payment_code]);

    const updateBillingMutation = useApiUpdateBillingData([
        selectedRecap === "stripe"
            ? "getAccountingStripeRecap"
            : selectedRecap === "cash"
                ? "getAccountingCashRecap"
                : "getAccountingBookingRecap",
        selectedYear,
        selectedMonth,
    ]);

    const updateNoteMutation = useApiUpdateNotAccountedNote([
        selectedRecap === "stripe"
            ? "getAccountingStripeRecap"
            : selectedRecap === "cash"
                ? "getAccountingCashRecap"
                : "getAccountingBookingRecap",
        selectedYear,
        selectedMonth,
    ]);

    const invalidateKeys = [
        selectedRecap === "stripe"
            ? "getAccountingStripeRecap"
            : selectedRecap === "cash"
                ? "getAccountingCashRecap"
                : "getAccountingBookingRecap",
        selectedYear,
        selectedMonth,
    ];

    const updateBookingStatusMutation = useApiUpdateBookingStatus([
        ...invalidateKeys,
        "getBookingsDetail"
    ]);

    const startEditCustomer = (customer: any) => {
        setEditingCustomerId(customer.billing_id);
        setEditError(null);
        setEditForm({
            cisco_id: customer.cisco_id ?? "",
            name: customer.name ?? "",
            address: customer.address ?? "",
            city: customer.city ?? "",
            zip: customer.zip ?? "",
            province: customer.province ?? "",
            vat: customer.vat ?? "",
            tax_id: customer.tax_id ?? "",
            payment_code: customer.cisco_payment_code ?? "",
            payment_method: customer.payment_method ?? "",
            unique_email: customer.unique_email ?? "",
        });
    };

    const cancelEdit = () => {
        setEditingCustomerId(null);
        setEditError(null);
    };

    const openCreateDialog = (prefill?: Partial<typeof createForm>) => {
        setCreateError(null);
        setCreateForm(prev => ({
            ris_id: "",
            cisco_id: "",
            company_name: "",
            contact_name: "",
            address: "",
            city: "",
            zip: "",
            province: "",
            vat: "",
            tax_id: "",
            payment_code: "",
            payment_method: "",
            unique_email: "",
            ...prefill,
        }));
        setIsCreateOpen(true);
    };

    const closeCreateDialog = () => {
        setIsCreateOpen(false);
        setCreateError(null);
    };

    const handleOpenNoteModal = (ris_id: number, note?: string) => {
        setCurrentNoteRisId(ris_id);
        setCurrentNote(note || "");
        setIsNoteModalOpen(true);
    };

    const handleSaveNote = (note: string) => {
        if (currentNoteRisId === null) return;
        updateNoteMutation.mutate(
            { ris_id: currentNoteRisId, note },
            {
                onSuccess: () => {
                    setIsNoteModalOpen(false);
                    setCurrentNote("");
                    setCurrentNoteRisId(null);
                },
            }
        );
    };

    const createBillingMutation = useApiCreateBillingData([
        selectedRecap === "stripe"
            ? "getAccountingStripeRecap"
            : selectedRecap === "cash"
                ? "getAccountingCashRecap"
                : "getAccountingBookingRecap",
        selectedYear,
        selectedMonth,
    ]);

    const submitCreate = () => {
        setCreateError(null);

        if (!createForm.company_name?.toString().trim()) {
            setCreateError("Il campo Ragione Sociale è obbligatorio.");
            return;
        }

        if (!createForm.vat || !createForm.vat.toString().trim()) {
            setCreateError("Il campo P.IVA è obbligatorio.");
            return;
        }
        if (!createForm.payment_code?.trim()) {
            setCreateError("Il campo Codice pagamento è obbligatorio.");
            return;
        }

        const selectedPaymentMethod = paymentMethods?.find((pm: any) => pm.official_code === createForm.payment_code);
        const paymentCodeToSend = selectedPaymentMethod ? selectedPaymentMethod.code.toString() : (createForm.payment_code);

        const payload: any = {
            ris_id: Number(createForm.ris_id),
            cisco_id: Number(createForm.cisco_id) || null,
            company_name: createForm.company_name?.trim(),
            contact_name: createForm.contact_name?.trim() || "",
            address: createForm.address?.toString() || null,
            city: createForm.city?.toString() || null,
            zip: createForm.zip?.toString() || null,
            province: createForm.province?.toString() || null,
            vat: createForm.vat?.toString(),
            tax_id: createForm.tax_id?.toString() || null,
            payment_code: paymentCodeToSend,
            payment_method: createForm.payment_method?.trim() || "",
            unique_email: createForm.unique_email?.trim() || null,
        };
        if (createForm.ris_id !== "") {
            payload.ris_id = Number(createForm.ris_id);
        }

        createBillingMutation.mutate(
            payload,
            {
                onSuccess: () => {
                    setIsCreateOpen(false);
                    setCreateError(null);
                },
                onError: (e: any) => {
                    setCreateError(e?.response?.data?.message || "Errore durante il salvataggio");
                },
            }
        );
    };

    const submitEdit = async (billing_id: number) => {
        setEditError(null);
        if (!editForm.name) {
            setEditError("Il campo Ragione Sociale è obbligatorio.");
            return;
        }
        if (!editForm.payment_code) {
            setEditError("I campi Codice pagamento e Metodo pagamento sono obbligatori.");
            return;
        }

        const selectedPaymentMethod = paymentMethods?.find((pm: any) => pm.official_code === editForm.payment_code);
        const paymentCodeToSend = selectedPaymentMethod ? selectedPaymentMethod.code.toString() : editForm.payment_code;

        updateBillingMutation.mutate(
            {
                billing_id,
                cisco_id: editForm.cisco_id || null,
                name: editForm.name.toString(),
                address: editForm.address?.toString() || null,
                city: editForm.city?.toString() || null,
                zip: editForm.zip?.toString() || null,
                province: editForm.province?.toString() || null,
                vat: editForm.vat?.toString() || null,
                tax_id: editForm.tax_id?.toString() || null,
                payment_code: paymentCodeToSend,
                payment_method: editForm.payment_method,
                unique_email: editForm.unique_email?.toString() || null,
            },
            {
                onSuccess: () => {
                    setEditingCustomerId(null);
                    setEditError(null);
                },
                onError: (e: any) => {
                    setEditError(e?.response?.data?.message || "Errore durante il salvataggio");
                },
            }
        );
    };

    const navigate = useNavigate();

    const stripeQuery = useApiGetAccountingStripeRecap(selectedYear, selectedMonth, {
        enabled: selectedRecap === "stripe",
    });

    const cashQuery = useApiGetAccountingCashRecap(selectedYear, selectedMonth, {
        enabled: selectedRecap === "cash",
    });

    const bookingQuery = useApiGetAccountingBookingRecap(selectedYear, selectedMonth, {
        enabled: selectedRecap === "booking",
    });

    const cashTransactionsDetailQuery = useApiGetCashTransactionsDetail(
        selectedRestaurantId ?? 0,
        selectedYear,
        selectedMonth,
        {
            enabled: selectedRecap === "cash" && selectedRestaurantId !== null,
        }
    );

    const {
        data: cashTransactionsDetail,
        isLoading: isCashDetailLoading,
        isError: isCashDetailError,
    } = cashTransactionsDetailQuery;

    const stripeTransactionsDetailQuery = useApiGetStripeTransactionsDetail(
        selectedRestaurantId ?? 0,
        selectedYear,
        selectedMonth,
        {
            enabled: selectedRecap === "stripe" && selectedRestaurantId !== null,
        }
    );

    const {
        data: stripeTransactionsDetail,
        isLoading: isStripeDetailLoading,
        isError: isStripeDetailError,
    } = stripeTransactionsDetailQuery;

    const bookingsDetailQuery = useApiGetBookingsDetail(
        selectedRestaurantId ?? 0,
        selectedYear,
        selectedMonth,
        {
            enabled: selectedRecap === "booking" && selectedRestaurantId !== null,
        }
    );

    const {
        data: bookingsDetail,
        isLoading: isBookingsDetailLoading,
        isError: isBookingsDetailError,
    } = bookingsDetailQuery;

    const activeQuery =
        selectedRecap === "stripe"
            ? stripeQuery
            : selectedRecap === "cash"
                ? cashQuery
                : bookingQuery;

    const { data, isLoading, isError, error } = activeQuery;

    const baseYear = 2024;
    const maxYearNum = currentYearNum + (currentMonthNum === 12 ? 1 : 0);

    const years = Array.from(
        { length: maxYearNum - baseYear + 1 },
        (_, i) => String(baseYear + i)
    );

    const months = [
        { value: "01", label: "Gennaio" },
        { value: "02", label: "Febbraio" },
        { value: "03", label: "Marzo" },
        { value: "04", label: "Aprile" },
        { value: "05", label: "Maggio" },
        { value: "06", label: "Giugno" },
        { value: "07", label: "Luglio" },
        { value: "08", label: "Agosto" },
        { value: "09", label: "Settembre" },
        { value: "10", label: "Ottobre" },
        { value: "11", label: "Novembre" },
        { value: "12", label: "Dicembre" },
    ];

    const toggleRow = (billing_id: string) => {
        setExpandedRows((prev) => ({
            ...prev,
            [billing_id]: !prev[billing_id],
        }));
        setSelectedRestaurantId(null);
    };

    const toggleRestaurant = (id: number) => {
        setExpandedRestaurants(prev => ({
            ...prev,
            [id]: !prev[id],
        }));
        setSelectedRestaurantId(id);
    };

    const restaurantColsCount = selectedRecap === "booking" || selectedRecap === "cash" ? 9 : 7;


    const handleExportRestaurant = (rest: any) => {
        const fileBase = `${selectedRecap}_${(rest?.name || "ristorante").replace(/[^a-z0-9_-]+/gi, "_")}_${selectedYear}-${selectedMonth}`;
        if (selectedRecap === "cash") {
            if (selectedRestaurantId !== rest.id || isCashDetailLoading || !cashTransactionsDetail || cashTransactionsDetail.length === 0) return;
        } else if (selectedRecap === "stripe") {
            if (selectedRestaurantId !== rest.id || isStripeDetailLoading || !stripeTransactionsDetail || stripeTransactionsDetail.length === 0) return;
        } else if (selectedRecap === "booking") {
            if (selectedRestaurantId !== rest.id || isBookingsDetailLoading || !bookingsDetail || bookingsDetail.length === 0) return;
        }
        const rows = buildCsvRowsForRestaurant(rest, {
            recap: selectedRecap,
            year: selectedYear,
            month: selectedMonth,
            cashTransactionsDetail,
            stripeTransactionsDetail,
            bookingsDetail,
        });
        const csv = toCSV(rows);
        const filename = `${fileBase}.csv`;
        downloadCSV(csv, filename);
    };

    const handleExportErrors = () => {
        if (!data?.errors || data.errors.length === 0) return;
        const rows = data.errors.map((e: any) => ({
            "RIS ID": e.ris_id,
            "Token": e.token ?? "",
            "Nome Ristorante": e.name ?? "",
            "Nome Ristoratore ": e.mycia_restaurateur.name ?? "",
            "P.IVA Ristoratore": e.mycia_restaurateur.vat_id ?? "",
            ...(selectedRecap === "booking" && {
                "Numero Prenotazioni": e.bookings_count,
                "Commissioni MyCia": e.mycia_commission_amount?.toFixed(2)  ?? "",
            }),
            "Note": e.note ?? "",
        }));
        const filename = `${selectedRecap}_non_contabilizzati_${selectedYear}-${selectedMonth}.csv`;
        const csv = toCSV(rows);
        downloadCSV(csv, filename);
    };

    // ... existing code ...
    const handleExportCustomers = () => {
        if (!data?.customers || data.customers.length === 0) return;

        if (selectedRecap === "cash") {
            type CashRow = {
                "Billing Ristoratore": string;
                "Billing P. Iva": string;
                "Ristorante": string;
                "Nr. Ordini": number | "";
                "Totale Ordini": string;
                "Perc. Commissione": string;
                "Commissione Totale": string;
            };

            const rows: CashRow[] = data.customers.flatMap((c: any): CashRow[] => {
                const restaurants: any[] = Array.isArray(c.restaurants) ? c.restaurants : [];
                if (restaurants.length === 0) {
                    return [{
                        "Billing Ristoratore": c.name ?? "",
                        "Billing P. Iva": c.vat ?? "",
                        "Ristorante": "",
                        "Nr. Ordini": "",
                        "Totale Ordini": "",
                        "Perc. Commissione": "",
                        "Commissione Totale": ""
                    }];
                }
                return restaurants.map((r: any): CashRow => ({
                    "Billing Ristoratore": c.name ?? "",
                    "Billing P. Iva": c.vat ?? "",
                    "Ristorante": r.name ?? "",
                    "Nr. Ordini": r?.cash_transactions_count ?? 0,
                    "Totale Ordini": r.total_amount.toFixed(2) ?? "",
                    "Perc. Commissione": r.cash_percentage.toFixed(2) ?? "",
                    "Commissione Totale": r.mycia_commission_amount.toFixed(2) ?? ""
                }));
            });

            const filename = `${selectedRecap}_ristoratori_${selectedYear}-${selectedMonth}.csv`;
            const csv = toCSV(rows);
            downloadCSV(csv, filename);
            return;
        }

        if (selectedRecap === "booking") {
            type BookingRow = {
                "Billing Ristoratore": string;
                "Billing P. Iva": string;
                "Ristorante": string;
                "Nr. Prenotazioni": number | "";
                "Nr. Posti": number | "";
                "Costo Unitario": string;
                "Comm. Totale": string;
            };

            const rows: BookingRow[] = data.customers.flatMap((c: any): BookingRow[] => {
                const restaurants: any[] = Array.isArray(c.restaurants) ? c.restaurants : [];
                if (restaurants.length === 0) {
                    return [{
                        "Billing Ristoratore": c.name ?? "",
                        "Billing P. Iva": c.vat ?? "",
                        "Ristorante": "",
                        "Nr. Prenotazioni": "",
                        "Nr. Posti": "",
                        "Costo Unitario": "",
                        "Comm. Totale": ""
                    }];
                }
                return restaurants.map((r: any): BookingRow => ({
                    "Billing Ristoratore": c.name ?? "",
                    "Billing P. Iva": c.vat ?? "",
                    "Ristorante": r.name ?? "",
                    "Nr. Prenotazioni": r?.bookings_count ?? 0,
                    "Nr. Posti": r?.seats ?? 0,
                    "Costo Unitario": r.single_booking_price.toFixed(2) ?? "",
                    "Comm. Totale":r.mycia_commission_amount.toFixed(2) ?? ""
                }));
            });

            const filename = `${selectedRecap}_ristoratori_${selectedYear}-${selectedMonth}.csv`;
            const csv = toCSV(rows);
            downloadCSV(csv, filename);
            return;
        }

        // stripe
        type StripeRow = {
            "Billing Ristoratore": string;
            "Billing P. Iva": string;
            "Ristorante": string;
            "Totale Importo Netto": string;
            "Totale Commissione": string;
        };

        const rows: StripeRow[] = data.customers.flatMap((c: any): StripeRow[] => {
            const restaurants: any[] = Array.isArray(c.restaurants) ? c.restaurants : [];
            if (restaurants.length === 0) {
                return [{
                    "Billing Ristoratore": c.name ?? "",
                    "Billing P. Iva": c.vat ?? "",
                    "Ristorante": "",
                    "Totale Importo Netto": "",
                    "Totale Commissione": ""
                }];
            }
            return restaurants.map((r: any): StripeRow => ({
                "Billing Ristoratore": c.name ?? "",
                "Billing P. Iva": c.vat ?? "",
                "Ristorante": r.name ?? "",
                "Totale Importo Netto": Number(r?.stripe_transactions_balance ?? 0).toFixed(2),
                "Totale Commissione": Number(r?.mycia_commission_amount ?? 0).toFixed(2)
            }));
        });

        const filename = `${selectedRecap}_ristoratori_${selectedYear}-${selectedMonth}.csv`;
        const csv = toCSV(rows);
        downloadCSV(csv, filename);
    };

    return (
        <div className="p-4">

            <div className="flex items-center justify-between gap-4 mb-2">
                <RecapTypeTabs value={selectedRecap} onChange={(t) => {
                    setSelectedRecap(t);
                    setRestaurantFilter("");
                    setExpandedRows({});
                    setExpandedRestaurants({});
                    setSelectedRestaurantId(null);
                    setShowErrors(false);
                    setShowTrend(false);
                }} />
                <div className="ml-auto flex items-center gap-2">
                    <button
                        type="button"
                        className={`px-3 py-1 rounded border flex items-center gap-2 ${showTrend ? "bg-mcia_orange text-white border-mcia_orange" : "bg-gray-100 hover:bg-gray-200"}`}
                        onClick={() => setShowTrend((v) => !v)}
                        title="Mostra andamento giornaliero"
                    >
                        <IoStatsChartOutline className="text-lg" />
                    </button>
                    <button
                        type="button"
                        className={`px-3 py-1 rounded border ${showErrors ? "bg-red-600 text-white border-red-600" : "bg-gray-100 hover:bg-gray-200"}`}
                        onClick={() => setShowErrors((v) => !v)}
                        title="Mostra gli elementi non contabilizzati"
                    >
                        Non Contabilizzati
                        {data?.errors && data.errors.length > 0 && (
                            <span className="ml-2 inline-flex items-center justify-center text-xs font-semibold rounded-full px-2 py-0.5 bg-red-100 text-red-700 border border-red-300">
                                {data.errors.length}
                                {data.total_not_accounted_mycia_commission_amount !== undefined && (
                                    <span className="ml-1 border-l border-red-300 pl-1">
                                        € {data.total_not_accounted_mycia_commission_amount.toFixed(2)}
                                    </span>
                                )}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            <div className="flex items-center justify-between gap-4 mb-2">
                <YearMonthPicker
                    year={selectedYear}
                    month={selectedMonth}
                    years={years}
                    months={months}
                    onYearChange={(y) => {
                        setSelectedYear(y);
                        setRestaurantFilter("");
                        setExpandedRows({});
                        setExpandedRestaurants({});
                        setSelectedRestaurantId(null);
                        setShowErrors(false);
                    }}
                    onMonthChange={(m) => {
                        setSelectedMonth(m);
                        setRestaurantFilter("");
                        setExpandedRows({});
                        setExpandedRestaurants({});
                        setSelectedRestaurantId(null);
                        setShowErrors(false);
                    }}
                />
                <div className="ml-auto flex items-center gap-2">
                    <button
                        type="button"
                        className={`inline-flex items-center gap-3 px-3 py-2 rounded-md border bg-mcia_orange text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition`}
                        onClick={() => navigate("/billing-data")}
                        title="Mostra la lista dei ristoratori"
                    >
                        <IoPeopleOutline className="text-lg" />
                        <span>Anagrafica Clienti</span>
                    </button>
                </div>
            </div>

            <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Ragione Sociale</label>
                <input
                    type="text"
                    value={restaurantFilter}
                    onChange={(e) => setRestaurantFilter(e.target.value)}
                    placeholder="Digita il nome della ragione sociale"
                    className="w-full max-w-md border px-3 py-2 rounded"
                />
            </div>

            {isLoading ? (
                <Loading />
            ) : isError ? (
                <div className="text-red-500">
                    Errore: {error?.response?.data?.message || "Unknown error"}
                </div>
            ) : data ? (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="p-4 border rounded shadow w-full"
                >
                    {/* Grafico Andamento Giornaliero */}
                    {showTrend && (
                        <DailyTrendChart
                            initialType={selectedRecap === "booking" ? "bookings" : selectedRecap}
                            initialYear={selectedYear}
                            initialMonth={selectedMonth}
                            years={years}
                            months={months}
                        />
                    )}

                    {/* Totali - mostra solo se ci sono customers */}
                    {data.customers && data.customers.length > 0 && (
                        <div className="mb-6">
                            <h2 className="text-xl font-bold mb-2">Totali</h2>
                            <div className="flex flex-col gap-2 text-sm">
                                <div>Clienti: {data.customers_count}</div>
                                {data.total_amount && (
                                    <div><span className="inline-block bg-gray-300 border border-gray-300 rounded px-2 py-0.5 text-sm">
                                            Totale importo: € {data.total_amount.toFixed(2)}
                                        </span></div>
                                )}
                                {data.total_mycia_commission_amount && (
                                    <div><span className="inline-block bg-mcia_orange border-mcia_orange text-white ounded px-2 py-0.5 text-sm">
                                            Commissioni MyCIA: € {data.total_mycia_commission_amount.toFixed(2)}
                                        </span></div>
                                )}
                                {data.total_cash_transactions_count && (
                                    <div>Transazioni Cash: {data.total_cash_transactions_count}</div>
                                )}
                                {data.total_bookings_count && (
                                    <div>Prenotazioni: {data.total_bookings_count} </div>
                                )}
                                {data.total_seats_count && (
                                    <div>Posti: {data.total_seats_count} </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Sezione Non Contabilizzati */}
                    {showErrors && (
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-2">
                                <div>
                                    <h2 className="text-lg font-bold">Non Contabilizzati</h2>
                                    <span className="block text-sm text-gray-500">
                                        Prenotazioni: {data.total_not_accounted_bookings_count}
                                    </span>
                                    <span className="block text-sm text-gray-500">
                                        Posti: {data.total_not_accounted_bookings_seats}
                                    </span>
                                </div>

                                <div className="flex items-center gap-2">
                                    {data?.errors && data.errors.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={handleExportErrors}
                                            className="inline-flex items-center gap-2 px-3 py-1 rounded border bg-gray-100 hover:bg-gray-200"
                                            title="Esporta tabella in CSV"
                                        >
                                            <IoDownloadOutline />
                                            <span>Esporta CSV</span>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {!data?.errors || data.errors.length === 0 ? (
                                <div className="text-sm text-gray-600">Nessun elemento non contabilizzato.</div>
                            ) : (
                                <div className="overflow-x-auto border rounded w-full">
                                    <table className="min-w-[600px] w-full divide-y divide-gray-300 text-sm">
                                        <thead className="bg-gray-100">
                                        <tr className="bg-red-600 text-white">
                                            <th className="px-4 py-2 text-left font-semibold">RIS ID</th>
                                            <th className="px-4 py-2 text-left font-semibold max-w-[200px]">Nome Ristorante</th>
                                            <th className="px-4 py-2 text-left font-semibold max-w-[200px]">Nome Ristoratore</th>
                                            <th className="px-4 py-2 text-left font-semibold">P.IVA Ristoratore</th>
                                            {selectedRecap === "booking" && (
                                                <>
                                                    <th className="px-4 py-2 text-left font-semibold">Prenotazioni</th>
                                                    <th className="px-4 py-2 text-left font-semibold">Posti</th>
                                                    <th className="px-4 py-2 text-left font-semibold">Commissioni MyCia</th>
                                                </>
                                            )}
                                            <th className="px-4 py-2 text-left font-semibold">Note</th>
                                            <th className="px-4 py-2 text-left font-semibold">Azioni</th>
                                        </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                        {data.errors.map((e, idx) => (
                                            <tr key={`${e.ris_id}-${idx}`} className="odd:bg-white even:bg-gray-50">
                                                <td className="px-4 py-2 whitespace-nowrap">{e.ris_id}</td>
                                                <td className="px-4 py-2 truncate max-w-[200px]" title={e.name}>{e.name}</td>
                                                <td className="px-4 py-2 truncate max-w-[200px]" title={e.mycia_restaurateur.name}>{e.mycia_restaurateur.name}</td>
                                                <td className="px-4 py-2 break-all">{e.mycia_restaurateur.vat_id}</td>
                                                {selectedRecap === "booking" && (
                                                    <>
                                                        <td className="px-4 py-2">{e.bookings_count}</td>
                                                        <td className="px-4 py-2">{e.seats}</td>
                                                        <td className="px-4 py-2"> € {e.mycia_commission_amount?.toFixed(2)}</td>
                                                    </>
                                                )}
                                                <td className="px-4 py-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="truncate max-w-[300px]" title={e.note}>{e.note || "-"}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleOpenNoteModal(e.ris_id, e.note)}
                                                            className="text-mcia_orange hover:opacity-80 transition-opacity"
                                                            title="Modifica Note"
                                                        >
                                                            <IoChatbubbleEllipsesOutline size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2">
                                                    <button
                                                        type="button"
                                                        className="px-3 py-1 rounded border bg-mcia_orange text-white hover:opacity-90"
                                                        onClick={() => openCreateDialog({
                                                            ris_id: e.ris_id,
                                                            vat: (e.mycia_restaurateur as any)?.vat_id || "",
                                                        })}
                                                    >
                                                        Inserisci
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tabella clienti */}
                    {data.customers && data.customers.length > 0 && (
                        <>
                            <div className="flex items-center justify-between mb-2">
                                <h2 className="text-lg font-bold">Clienti</h2>
                                <div className="flex items-center gap-2">
                                    {data?.customers && data.customers.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={handleExportCustomers}
                                            className="inline-flex items-center gap-2 px-3 py-1 rounded border bg-gray-100 hover:bg-gray-200"
                                            title="Esporta tabella in CSV"
                                        >
                                            <IoDownloadOutline />
                                            <span>Esporta CSV</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        <div className="overflow-x-auto border rounded w-full">
                            <table className="min-w-[1200px] w-full divide-y divide-gray-300 text-sm">
                            <thead className="bg-gray-100">
                            <tr className="bg-mcia_orange text-white hover:text-black">
                                <th className="px-2 py-2 w-8"></th>
                                <th className="px-4 py-2 text-left font-semibold">Nome</th>
                                <th className="px-4 py-2 text-left font-semibold">Email</th>
                                <th className="px-4 py-2 text-left font-semibold">Commissioni</th>
                                {(selectedRecap === "stripe" || selectedRecap === "cash") && (
                                    <th className="px-4 py-2 text-left font-semibold">Transazioni</th>
                                )}
                                {selectedRecap === "booking" && (
                                    <th className="px-4 py-2 text-left font-semibold">Prenotazioni</th>
                                )}
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-300">
                            {data.customers
                                .filter((customer: any) => {
                                    const q = restaurantFilter.trim().toLowerCase();
                                    if (!q) return true;
                                    const customerMatch = (customer.name ?? "").toLowerCase().includes(q);
                                    const restaurantMatch = customer.restaurants?.some(
                                        (r: any) => (r.name ?? "").toLowerCase().includes(q)
                                    );
                                    return customerMatch || restaurantMatch;
                                })
                                .map((customer: any) => {
                                    const isExpanded = expandedRows[customer.billing_id] || false;
                                    const q = restaurantFilter.trim().toLowerCase();
                                    const customerMatch = q
                                        ? (customer.name ?? "").toLowerCase().includes(q)
                                        : false;


                                    const filteredRestaurants = !q || customerMatch
                                        ? customer.restaurants
                                        : customer.restaurants.filter((r: any) =>
                                            (r.name ?? "").toLowerCase().includes(q)
                                        );

                                    return (
                                        <React.Fragment key={customer.billing_id}>
                                            {/* Riga cliente */}
                                            <tr
                                                onClick={() => toggleRow(customer.billing_id.toString())}
                                                className="cursor-pointer hover:bg-mcia_salmon transition odd:bg-white even:bg-gray-200"
                                            >
                                                <td className="px-2 py-2 w-8"
                                                    onClick={(e) => { e.stopPropagation(); toggleRow(customer.billing_id.toString()); }}>
                                                    <ExpandIcon expanded={isExpanded} />
                                                </td>
                                                <td className="px-4 py-2 font-medium">{customer.name}</td>
                                                <td className="px-4 py-2">{customer.email}</td>
                                                <td className="px-4 py-2">
                                                    {customer.customer_mycia_commission_amount
                                                        ? `€ ${customer.customer_mycia_commission_amount.toFixed(2)}`
                                                        : "-"}
                                                </td>
                                                {(selectedRecap === "stripe" || selectedRecap === "cash") && (
                                                    <td className="px-4 py-2">
                                                        {(customer.customer_transactions_count ?? 0) +
                                                            (customer.customer_stripe_transactions_count ?? 0) +
                                                            (customer.customer_cash_transactions_count ?? 0)}
                                                    </td>
                                                )}
                                                {selectedRecap === "booking" && (
                                                    <td className="px-4 py-2">{customer.customer_bookings_count ?? 0}</td>
                                                )}
                                            </tr>

                                            {/* Riga espansa cliente */}
                                            <AnimatePresence>
                                                {isExpanded && (
                                                    <motion.tr
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: "auto" }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        transition={{ duration: 0.25 }}
                                                        className="bg-gray-50"
                                                    >
                                                        <td colSpan={6} className="px-4 py-3">
                                                            <div className="space-y-3">
                                                                {/* Billing data - view or edit */}
                                                                {editingCustomerId === customer.billing_id ? (
                                                                    <BillingEditForm
                                                                        form={editForm as any}
                                                                        setForm={setEditForm as any}
                                                                        error={editError}
                                                                        onSaveClick={(e) => { e.stopPropagation(); submitEdit(customer.billing_id); }}
                                                                        onCancelClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                                                                        paymentMethods={pmList}
                                                                        isPmLoading={isPmLoading}
                                                                        isPmError={isPmError}
                                                                        isSaving={updateBillingMutation.isLoading}
                                                                    />
                                                                ) : (
                                                                    <div>
                                                                        <div className="flex items-center justify-between">
                                                                            <div className="space-y-1 text-sm">
                                                                                <p><span className="text-xs text-mcia_orange"><strong>Indirizzo</strong></span>: {customer.address || "N/D"}</p>
                                                                                <p><span className="text-xs text-mcia_orange"><strong>Cisco ID</strong></span>: {customer.cisco_id || "N/D"}</p>
                                                                                <p><span className="text-xs text-mcia_orange"><strong>P.IVA</strong></span>: {customer.vat || "N/D"}</p>
                                                                                <p><span className="text-xs text-mcia_orange"><strong>Cod. Fiscale</strong></span>: {customer.tax_id || "N/D"}</p>
                                                                                <p><span className="text-xs text-mcia_orange"><strong>Telefono</strong></span>: {customer.phone || "N/D"}</p>
                                                                                <p><span className="text-xs text-mcia_orange"><strong>Codice Pagamenti</strong></span>: {customer.payment_code || "N/D"}</p>
                                                                                <p><span className="text-xs text-mcia_orange"><strong>Metodo Pagamenti</strong></span>: {customer.payment_method || "N/D"}</p>
                                                                                <p><span className="text-xs text-mcia_orange"><strong>Email Univoca</strong></span>: {customer.unique_email || "N/D"}</p>
                                                                            </div>
                                                                            <div>
                                                                                <button
                                                                                    type="button"
                                                                                    className="px-3 py-1 rounded border bg-gray-100 hover:bg-gray-200"
                                                                                    onClick={(e) => { e.stopPropagation(); startEditCustomer(customer); }}
                                                                                >
                                                                                    Modifica dati fatturazione
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {filteredRestaurants.length > 0 ? (
                                                                    <table className="w-full text-xs border rounded mt-2">
                                                                        <thead className="bg-mcia_orange text-white hover:text-black">
                                                                        <tr>
                                                                            <th className="px-2 py-2 w-9"></th>
                                                                            <th className="px-4 py-2 text-left font-semibold">RIS ID</th>
                                                                            <th className="px-2 py-1 text-left">Nome</th>
                                                                            <th className="px-2 py-1 text-left">P.IVA</th>
                                                                            {selectedRecap === "cash" && (
                                                                                <>
                                                                                    <th className="px-2 py-1 text-left">Ordini</th>
                                                                                    <th className="px-2 py-1 text-left">Totale</th>
                                                                                    <th className="px-2 py-1 text-left">Percentuale Comm.</th>
                                                                                </>
                                                                            )}
                                                                            {selectedRecap === "stripe" && (
                                                                                <th className="px-2 py-1 text-left">Stripe</th>
                                                                            )}
                                                                            {selectedRecap === "booking" && (
                                                                                <>
                                                                                    <th className="px-2 py-1 text-left">Prenotazioni</th>
                                                                                    <th className="px-2 py-1 text-left">Posti</th>
                                                                                    <th className="px-2 py-1 text-left">Costo Unitario</th>
                                                                                </>
                                                                            )}
                                                                            <th className="px-2 py-1 text-left">Comm. Totale</th>
                                                                            <th className="px-2 py-1 text-left">Export</th>
                                                                        </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                        {filteredRestaurants.map((rest: any) => {
                                                                            const isRestExpanded = expandedRestaurants[rest.id] || false;
                                                                            return (
                                                                                <React.Fragment key={rest.id}>
                                                                                    <tr
                                                                                        className="border-t cursor-pointer hover:bg-mcia_salmon transition odd:bg-white even:bg-gray-200"
                                                                                        onClick={() => toggleRestaurant(rest.id)}
                                                                                    >
                                                                                        <td className="px-2 py-2 w-9"
                                                                                            onClick={(e) => { e.stopPropagation(); toggleRow(rest.id.toString()); }}>
                                                                                            <ExpandIcon expanded={isRestExpanded} />
                                                                                        </td>
                                                                                        <td className="px-2 py-1">{rest.id}</td>
                                                                                        <td className="px-2 py-1">{rest.name}</td>
                                                                                        <td className="px-2 py-1">{rest.piva || "N/A"}</td>
                                                                                        {selectedRecap === "cash" && (
                                                                                            <>
                                                                                                <td className="px-2 py-1">{rest.cash_transactions_count ?? 0}</td>
                                                                                                <td className="px-2 py-1">€ {(rest.total_amount ?? 0).toFixed(2)}</td>
                                                                                                <td className="px-2 py-1">{(rest.cash_percentage ?? 0)} %</td>
                                                                                            </>
                                                                                        )}
                                                                                        {selectedRecap === "stripe" && (
                                                                                            <td className="px-2 py-1">{rest.stripe_transactions_count ?? 0}</td>
                                                                                        )}
                                                                                        {selectedRecap === "booking" && (
                                                                                            <>
                                                                                                <td className="px-2 py-1">{rest.bookings_count ?? 0}</td>
                                                                                                <td className="px-2 py-1">{rest.seats ?? 0}</td>
                                                                                                <td className="px-2 py-1">€ {(rest.single_booking_price ?? 0).toFixed(2)}</td>
                                                                                            </>
                                                                                        )}
                                                                                        <td className="px-2 py-1">€ {(rest.mycia_commission_amount ?? 0).toFixed(2)}</td>
                                                                                        {/* Export */}
                                                                                        <td className="px-2 py-1">
                                                                                            {(() => {
                                                                                                const exportReady = selectedRecap === "cash"
                                                                                                    ? (selectedRestaurantId === rest.id && !isCashDetailLoading && !!cashTransactionsDetail && cashTransactionsDetail.length > 0)
                                                                                                    : selectedRecap === "stripe"
                                                                                                        ? (selectedRestaurantId === rest.id && !isStripeDetailLoading && !!stripeTransactionsDetail && stripeTransactionsDetail.length > 0)
                                                                                                        : (selectedRestaurantId === rest.id && !isBookingsDetailLoading && !!bookingsDetail && bookingsDetail.length > 0);
                                                                                                const title = exportReady
                                                                                                    ? "Esporta CSV"
                                                                                                    : "Espandi la riga e attendi il caricamento dei dettagli per esportare";
                                                                                                return (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        title={title}
                                                                                                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border ${exportReady ? "bg-white hover:bg-gray-100 text-gray-700" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
                                                                                                        onClick={(e) => { e.stopPropagation(); if (exportReady) handleExportRestaurant(rest); }}
                                                                                                        disabled={!exportReady}
                                                                                                    >
                                                                                                        <IoDownloadOutline />
                                                                                                        CSV
                                                                                                    </button>
                                                                                                );
                                                                                            })()}
                                                                                        </td>
                                                                                    </tr>

                                                                                    {/* Dettagli per riga ristorante */}
                                                                                    <AnimatePresence>
                                                                                        {isRestExpanded && selectedRecap === "cash" && selectedRestaurantId === rest.id && (
                                                                                            <motion.tr
                                                                                                initial={{ opacity: 0, height: 0 }}
                                                                                                animate={{ opacity: 1, height: "auto" }}
                                                                                                exit={{ opacity: 0, height: 0 }}
                                                                                                transition={{ duration: 0.25 }}
                                                                                                className="bg-gray-100"
                                                                                            >
                                                                                                <td colSpan={restaurantColsCount} className="px-2 py-2">
                                                                                                    {isCashDetailLoading ? (
                                                                                                        <p className="text-xs text-gray-500">Caricamento dettagli…</p>
                                                                                                    ) : isCashDetailError ? (
                                                                                                        <p className="text-xs text-red-500">Errore nel caricamento dei dettagli.</p>
                                                                                                    ) : (
                                                                                                        <CashDetailTable rows={cashTransactionsDetail || []} />
                                                                                                    )}
                                                                                                </td>
                                                                                            </motion.tr>
                                                                                        )}
                                                                                    </AnimatePresence>
                                                                                    <AnimatePresence>
                                                                                        {isRestExpanded && selectedRecap === "stripe" && selectedRestaurantId === rest.id && (
                                                                                            <motion.tr
                                                                                                initial={{ opacity: 0, height: 0 }}
                                                                                                animate={{ opacity: 1, height: "auto" }}
                                                                                                exit={{ opacity: 0, height: 0 }}
                                                                                                transition={{ duration: 0.25 }}
                                                                                                className="bg-gray-100"
                                                                                            >
                                                                                                <td colSpan={restaurantColsCount} className="px-2 py-2">
                                                                                                    {isStripeDetailLoading ? (
                                                                                                        <p className="text-xs text-gray-500">Caricamento dettagli…</p>
                                                                                                    ) : isStripeDetailError ? (
                                                                                                        <p className="text-xs text-red-500">Errore nel caricamento dei dettagli.</p>
                                                                                                    ) : (
                                                                                                        <StripeDetailTable rows={stripeTransactionsDetail || []} />
                                                                                                    )}
                                                                                                </td>
                                                                                            </motion.tr>
                                                                                        )}
                                                                                    </AnimatePresence>
                                                                                    <AnimatePresence>
                                                                                        {isRestExpanded && selectedRecap === "booking" && selectedRestaurantId === rest.id && (
                                                                                            <motion.tr
                                                                                                initial={{ opacity: 0, height: 0 }}
                                                                                                animate={{ opacity: 1, height: "auto" }}
                                                                                                exit={{ opacity: 0, height: 0 }}
                                                                                                transition={{ duration: 0.25 }}
                                                                                                className="bg-gray-100"
                                                                                            >
                                                                                                <td colSpan={restaurantColsCount} className="px-2 py-2">
                                                                                                    {isBookingsDetailLoading ? (
                                                                                                        <p className="text-xs text-gray-500">Caricamento dettagli…</p>
                                                                                                    ) : isBookingsDetailError ? (
                                                                                                        <p className="text-xs text-red-500">Errore nel caricamento dei dettagli.</p>
                                                                                                    ) : (
                                                                                                        <BookingDetailTable 
                                                                                                            rows={bookingsDetail || []} 
                                                                                                            onUpdateStatus={(booking_id, status) => {
                                                                                                                if (selectedRestaurantId) {
                                                                                                                    updateBookingStatusMutation.mutate({
                                                                                                                        ris_id: selectedRestaurantId,
                                                                                                                        booking_id,
                                                                                                                        status
                                                                                                                    });
                                                                                                                }
                                                                                                            }}
                                                                                                            isUpdating={updateBookingStatusMutation.isLoading}
                                                                                                        />
                                                                                                    )}
                                                                                                </td>
                                                                                            </motion.tr>
                                                                                        )}
                                                                                    </AnimatePresence>
                                                                                </React.Fragment>
                                                                            );
                                                                        })}
                                                                        </tbody>
                                                                    </table>
                                                                ) : (
                                                                    <p className="text-xs text-gray-500 mt-2">
                                                                        Nessun ristorante corrisponde al filtro.
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </motion.tr>
                                                )}
                                            </AnimatePresence>
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                            </table>

                            {data.customers.filter((customer: any) => {
                                const q = restaurantFilter.trim().toLowerCase();
                                if (!q) return true;
                                const customerMatch = (customer.name ?? "").toLowerCase().includes(q);
                                const restaurantMatch = customer.restaurants?.some(
                                    (r: any) => (r.name ?? "").toLowerCase().includes(q)
                                );
                                return customerMatch || restaurantMatch;
                            }).length === 0 && (
                                <div className="px-4 py-3 text-sm text-gray-600">
                                    Nessun cliente corrisponde al filtro.
                                </div>
                            )}
                        </div>
                        </>
                    )}
                    {(!data.customers || data.customers.length === 0) && (!data.errors || data.errors.length === 0) && (
                        <div className="text-gray-500">Nessun dato disponibile</div>
                    )}
                </motion.div>
            ) : (
                <div>Nessun dato disponibile</div>
            )}

            {isCreateOpen && (
                <CreateBillingModal
                    open={isCreateOpen}
                    form={createForm as any}
                    setForm={setCreateForm as any}
                    error={createError}
                    onClose={closeCreateDialog}
                    onSubmit={submitCreate}
                    paymentMethods={pmList}
                    isPmLoading={isPmLoading}
                    isPmError={isPmError}
                    isSaving={createBillingMutation.isLoading}
                    restaurants={restaurants}
                    restaurantsLoading={isRestaurantsLoading}
                    restaurantsError={isRestaurantsError}
                    readOnlyRisId={true}
                />
            )}

            <EditNoteModal
                open={isNoteModalOpen}
                initialNote={currentNote}
                onClose={() => setIsNoteModalOpen(false)}
                onSave={handleSaveNote}
                isSaving={updateNoteMutation.isLoading}
            />
        </div>
    );
}

export default AccountingRecapPage;
